import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { digObject, launchModal } from '@domain-group/javascript-utils'

import mapProviderToModel from '@functions/provider'
import { loginAsEntityUser, mergeAndStringifyEntityObjects } from '@functions/entity'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as providerActions from '@redux/modules/provider'

import defaultFormState, { requiredFields } from '@models/provider'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { ModuleState } from '@redux/modules/types'
import type { ProviderModel, ProviderRequestOptions } from '@models/types'

const stringifyObjectKeys: (keyof ProviderModel)[] = ['data', 'options']

export const generateUrls = (provider?: Partial<ProviderModel>) => {
  const { id } = provider || {}

  return {
    editProviderRelationsUrl: `#/systemManager/providers/${id}/edit/relations`,
    providerSalesforceDetailsUrl: `#/systemManager/providers/${id}/edit/salesforceDetails`,
    editProviderUrl: `#/systemManager/providers/${id}/edit`,
    inactiveProvidersIndexUrl: '#/systemManager/providers?active=false',
    providersIndexUrl: '#/systemManager/providers?active=true',
  }
}

type CreateProviderParams = {
  dispatch: AppDispatch,
  providerParams: Partial<ProviderModel>,
  requestOptions?: ProviderRequestOptions,
}

const createProvider = (params: CreateProviderParams) => {
  const { dispatch, providerParams, requestOptions } = params
  const { createProvider: createFn } = providerActions

  const updatedParams = mergeAndStringifyEntityObjects({}, providerParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      // @ts-expect-error TS2339
      const { data } = response
      const { editProviderUrl } = generateUrls(data)

      return {
        success,
        data,
        redirectUrl: editProviderUrl,
      }
    }

    return response
  })
}

type UpdateProviderParams = {
  dispatch: AppDispatch,
  provider: ProviderModel,
  providerParams: Partial<ProviderModel>,
  requestOptions?: ProviderRequestOptions,
}

const updateProvider = (params: UpdateProviderParams) => {
  const {
    dispatch, provider, providerParams, requestOptions,
  } = params
  const { updateProvider: updateFn } = providerActions

  const updatedParams = mergeAndStringifyEntityObjects(provider, providerParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

type LoginAsProviderParams = {
  dispatch: AppDispatch,
  provider: ProviderModel,
}

const loginAsProvider = (params: LoginAsProviderParams) => {
  const { provider, dispatch } = params

  return dispatch(loginAsEntityUser('providers', provider.id, {})).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      return {
        success,
        // @ts-expect-error TS2339
        redirectUrl: data.url,
      }
    }

    return response
  })
}

type ExportProviderParams = {
  dispatch: AppDispatch,
  provider: ProviderModel,
}

export const exportProvider = (params: ExportProviderParams) => {
  const { provider, dispatch } = params
  const { exportProvider: exportFn } = providerActions

  return dispatch(exportFn(provider))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useProviderForm(
  provider: Partial<ProviderModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const providerForm = useForm(
    defaultFormState,
    { entity: mapProviderToModel(provider), requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [provider.id, provider.cache_key],
  )

  return {
    ...providerForm,
  }
}

export type UploadLogoCustomPayload = {
  type?: string,
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: ProviderRequestOptions,
}

export type DeepLink = {
  id: string,
  link_text: string,
  method: string,
  status_id?: string,
  title: string,
  url: string,
}

function useProvider(initEntity: Partial<ProviderModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: provider }: { entity: ProviderModel} = useLatestEntity(initEntity, 'providers')
  const { id } = provider

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  useReduxAction(
    'providers',
    'loadProvider',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const { creating, loading, updating } = useSelector(reduxState => reduxState.providers)

  const alternateLogoUrl = digObject(provider, 'alternate_logo.thumb')
  const logoUrl = digObject(provider, 'logo.thumb')
  const isPreferred = digObject(provider, 'options.preferred_supplier', false)

  const quickLinks = digObject(provider, 'options.quick_links', [])
  const hasQuickLinks = !!quickLinks.length

  const deepLinks: DeepLink[] = digObject(provider, 'options.deep_links', [])
  const hasDeepLinks = !!deepLinks.length

  const providerSalesforceDetails = provider?.salesforce_details ?? {}

  return {
    alternateLogoUrl,
    callbacks: {
      createProvider: (providerParams: Partial<ProviderModel>, entityOptions?: ProviderRequestOptions) => (
        createProvider({ providerParams, dispatch, requestOptions: entityOptions })
      ),
      editProviderCustomInvoice: () => launchModal({
        callbacks,
        modalKey: 'EditProviderCustomInvoiceModal',
        payload: { provider },
      }),
      createOrEditCustomLinks: (customPayload: {}) => launchModal({
        callbacks,
        modalKey: 'ManageEntityQuickLinksModal',
        payload: { ...customPayload, entity: provider, entityKey: 'providers' },
      }),
      editProviderTradingDays: () => launchModal({
        callbacks,
        modalKey: 'EditProviderTradingDaysModal',
        payload: { provider },
      }),
      exportProvider: () => exportProvider({ provider, dispatch }),
      generateReport: () => launchModal({
        callbacks,
        modalKey: 'GenerateReportModal',
        payload: {
          allowedReports: ['approved_orders', 'processing_fees'],
          formDeps: [provider.id],
          defaultValues: { provider_id: provider.id },
        },
      }),
      loginAsProvider: () => loginAsProvider({ provider, dispatch }),
      manageProviderApiSettings: () => launchModal({
        callbacks,
        modalKey: 'ManageProviderApiSettingsModal',
        payload: { provider },
      }),
      manageProviderOptions: () => launchModal({
        callbacks,
        modalKey: 'ManageProviderOptionsModal',
        payload: { provider },
      }),
      manageProviderOrderDelivery: () => launchModal({
        callbacks,
        modalKey: 'ManageProviderOrderDeliveryModal',
        payload: { provider },
      }),
      updateProvider: (providerParams: Partial<ProviderModel>, entityOptions?: ProviderRequestOptions) => (
        updateProvider({
          provider, providerParams, dispatch, requestOptions: entityOptions,
        })
      ),
      uploadLogo: (customPayload?: UploadLogoCustomPayload) => launchModal({
        callbacks,
        modalKey: 'UploadProviderLogoModal',
        payload: { ...customPayload, provider },
      }),
    },
    creating,
    deepLinks,
    hasDeepLinks,
    hasQuickLinks,
    isPreferred,
    loading,
    logoUrl,
    provider,
    providerSalesforceDetails,
    quickLinks,
    updating,
    urls: generateUrls(provider),
  }
}

export type UseProviderPayload = ReturnType<typeof useProvider>

export default useProvider
